/**
 * Page sharing & permissions (contracts sharing.ts), pure and translated to Swift/Kotlin.
 *
 * `resolvePageAccess` is THE access rule: the API (WorkspaceDO docs) and the clients run this same function, so the
 * Share dialog, the sidebar and the server always agree. Order, from the page up to the root of its subtree:
 *   restricted members (guests, agents) → only inside their `pageIds` subtrees, at the nearest share (default edit;
 *     never through general access, and in private sections only through a share);
 *   private section → its owner (full); else the nearest explicit share; else the nearest general access ("Everyone
 *     at <workspace>", like Notion: the page stays in its owner's Private section and shows under "Shared with me"
 *     for everyone else); else nothing (admins included);
 *   owners/admins → full; nearest explicit share (own beats inherited); creator of the page or a page above → full;
 *   nearest general access ("none" ⇒ nothing); workspace default → edit.
 *
 * Everything else here derives what the Share dialog shows: who may do what (`sharingCapabilities`), the rows of
 * "People with access", the general-access options, labels, the top-bar indicator and the sidebar badge.
 */
import type {
  Channel,
  ChannelMembership,
  GeneralAccess,
  Member,
  PageAccess,
  PageNode,
  PagePermission,
  PageSharing,
  PageVisibility,
  PermissionSource,
  PublishPolicy,
  Role,
  SharingSettings,
} from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";

// ---------- Levels ----------

/** Lowest to highest. */
export const LEVELS: PageAccess[] = ["view", "comment", "edit", "full"];

/** "Everyone at <workspace>" choices, most to least access, then "only people invited". */
export const GENERAL_LEVELS: GeneralAccess[] = ["full", "edit", "comment", "view", "none"];

/** What members get on a workspace page when nothing more specific applies. */
export const DEFAULT_LEVEL: PageAccess = "edit";

export const DEFAULT_SETTINGS: SharingSettings = { publicPages: "members", publicFiles: "admins", editorsCanShare: true };

export function levelRank(a: PageAccess): number {
  return LEVELS.indexOf(a);
}

export function atLeast(a: PageAccess | undefined, min: PageAccess): boolean {
  return a !== undefined && levelRank(a) >= levelRank(min);
}

/** Comments don't exist yet: "comment" behaves exactly like "view" (read-only). */
export function canEditLevel(a: PageAccess | undefined): boolean {
  return atLeast(a, "edit");
}

// ---------- Resolution ----------

/** Who is asking: role, and for restricted members (guests, agents) the subtrees shared with them. */
export type AccessSubject = { userId: string; role: Role; restricted: boolean; pageIds?: string[] };

/** One node of a page's chain (the page first, its root last). `general` = this node's own setting. */
export type ChainNode = { id: string; createdBy: string; visibility: PageVisibility; general?: GeneralAccess };

/**
 * Why the access is what it is. "share"/"inherited": an explicit share on this page / on `fromPageId` above;
 * "guest": a restricted member's pageIds grant without a share (default edit); "general": general access of
 * `fromPageId` (or this page); "default": the workspace default (edit).
 */
export type AccessSource = "owner" | "admin" | "creator" | "share" | "inherited" | "guest" | "general" | "default" | "none";

export type AccessResolution = { access?: PageAccess; source: AccessSource; fromPageId?: string };

/** Restricted for docs: an explicit restricted policy, or the guest role. */
export function isRestrictedSubject(role: Role, restrictedPolicy: boolean): boolean {
  return restrictedPolicy || role === "guest";
}

function nearestShare(chain: ChainNode[], shares: Record<string, PageAccess>): { index: number; access: PageAccess } | undefined {
  for (let i = 0; i < chain.length; i++) {
    const a = shares[chain[i]!.id];
    if (a !== undefined) return { index: i, access: a };
  }
  return undefined;
}

function shareResolution(chain: ChainNode[], s: { index: number; access: PageAccess }): AccessResolution {
  return s.index === 0 ? { access: s.access, source: "share" } : { access: s.access, source: "inherited", fromPageId: chain[s.index]!.id };
}

/**
 * Access of `subject` to `chain[0]` (`chain` = [page, parent, …, root]; rows continue through their database).
 * `shares` = the subject's explicit shares by page id.
 */
export function resolvePageAccess(subject: AccessSubject, chain: ChainNode[], shares: Record<string, PageAccess>): AccessResolution {
  const root = chain.at(-1);
  if (root === undefined) return { source: "none" };
  const share = nearestShare(chain, shares);
  const restricted = isRestrictedSubject(subject.role, subject.restricted);
  if (restricted) {
    const granted = subject.pageIds ?? [];
    const grant = chain.findIndex((p) => granted.includes(p.id));
    if (grant < 0) return { source: "none" };
    if (share !== undefined) return shareResolution(chain, share);
    if (root.visibility === "private") return { source: "none" };
    return grant === 0 ? { access: "edit", source: "guest" } : { access: "edit", source: "guest", fromPageId: chain[grant]!.id };
  }
  if (root.visibility === "private") {
    if (root.createdBy === subject.userId) return { access: "full", source: "creator" };
    if (share !== undefined) return shareResolution(chain, share);
    return generalResolution(chain) ?? { source: "none" };
  }
  if (subject.role === "owner") return { access: "full", source: "owner" };
  if (subject.role === "admin") return { access: "full", source: "admin" };
  if (share !== undefined) return shareResolution(chain, share);
  if (chain.some((p) => p.createdBy === subject.userId)) return { access: "full", source: "creator" };
  return generalResolution(chain) ?? { access: DEFAULT_LEVEL, source: "default" };
}

/** The nearest general access of the chain as a resolution (undefined when no page sets one). */
function generalResolution(chain: ChainNode[]): AccessResolution | undefined {
  const g = chain.findIndex((p) => p.general !== undefined);
  if (g < 0) return undefined;
  const general = chain[g]!.general!;
  const r: AccessResolution = general === "none" ? { source: "none" } : { access: general, source: "general" };
  if (g > 0) r.fromPageId = chain[g]!.id;
  return r;
}

/**
 * The general access in effect on `chain[0]` and where it comes from. Without one set, workspace pages default to
 * edit and private sections to "none" (only people invited).
 */
export function effectiveGeneral(chain: ChainNode[]): { general: GeneralAccess; source: "page" | "inherited" | "default"; fromPageId?: string } {
  const root = chain.at(-1);
  if (root === undefined) return { general: "none", source: "default" };
  const g = chain.findIndex((p) => p.general !== undefined);
  if (g < 0) return { general: root.visibility === "private" ? "none" : DEFAULT_LEVEL, source: "default" };
  return g === 0 ? { general: chain[0]!.general!, source: "page" } : { general: chain[g]!.general!, source: "inherited", fromPageId: chain[g]!.id };
}

/**
 * How a resolved access shows up in "People with access" (undefined: it doesn't — general/default access is the
 * "Everyone at" row). A guest's pageIds grant without a share counts as a share where it was granted.
 */
export function listedSource(r: AccessResolution): PermissionSource | undefined {
  switch (r.source) {
    case "owner":
    case "admin":
    case "creator":
    case "share":
    case "inherited":
      return r.source;
    case "guest":
      return r.fromPageId === undefined ? "share" : "inherited";
    default:
      return undefined;
  }
}

// ---------- What each level allows ----------

export type SharingCapabilities = {
  canView: boolean;
  /** Comments don't exist yet: always false (comment access reads like view). */
  canComment: boolean;
  canEdit: boolean;
  /** Change or remove people, general access (full access). */
  canShare: boolean;
  /** Invite people (full; edit when the workspace lets editors share). */
  canInvite: boolean;
  /** Levels I may grant when inviting. */
  inviteLevels: PageAccess[];
  /** Invite people from outside the workspace as guests (not guests or agents themselves). */
  canInviteGuests: boolean;
  /** Change "Everyone at <workspace>" (full access, unrestricted). */
  canChangeGeneral: boolean;
  /** Publish to the web / change or revoke the public link. */
  canPublish: boolean;
  /** Lock / unlock the page. */
  canLock: boolean;
};

export type CapabilityInput = {
  access?: PageAccess;
  role: Role;
  /** Restricted member (guest or agent). */
  restricted: boolean;
  settings: SharingSettings;
};

/** Whether `role` may create links under `policy` (restricted members never may). */
export function policyAllows(policy: PublishPolicy, role: Role, restricted: boolean): boolean {
  if (restricted || role === "guest" || policy === "off") return false;
  return policy === "members" || role === "owner" || role === "admin";
}

export function sharingCapabilities(i: CapabilityInput): SharingCapabilities {
  const restricted = isRestrictedSubject(i.role, i.restricted);
  const full = i.access === "full";
  const edit = atLeast(i.access, "edit");
  const canInvite = full || (edit && i.settings.editorsCanShare && !restricted);
  let inviteLevels: PageAccess[] = [];
  if (full) inviteLevels = ["full", "edit", "comment", "view"];
  else if (canInvite) inviteLevels = ["edit", "comment", "view"];
  return {
    canView: i.access !== undefined,
    canComment: false,
    canEdit: edit,
    canShare: full,
    canInvite,
    inviteLevels,
    canInviteGuests: canInvite && !restricted,
    canChangeGeneral: full && !restricted,
    canPublish: full && policyAllows(i.settings.publicPages, i.role, restricted),
    canLock: edit,
  };
}

/** Why publishing is unavailable to someone with full access (undefined when it's available or irrelevant). */
export function publishBlockedReason(settings: SharingSettings, role: Role, restricted: boolean): MessageDescriptor | undefined {
  if (settings.publicPages === "off") return msg("sharing.publish.off");
  if (!policyAllows(settings.publicPages, role, restricted)) return msg("sharing.publish.adminsOnly");
  return undefined;
}

// ---------- Changes ----------

/**
 * A sharing change that would leave nobody active with full access: the last full access is kept. `people` = the
 * current list, `userId` the person changed, `next` their new level (undefined = removed). Returns a problem code.
 */
export function lastFullAccessProblem(people: PagePermission[], userId: string, next: PageAccess | undefined): "last_full_access" | undefined {
  const fullBefore = people.filter((p) => p.access === "full");
  if (next === "full") return undefined;
  const others = fullBefore.filter((p) => p.userId !== userId);
  return fullBefore.some((p) => p.userId === userId) && others.length === 0 ? "last_full_access" : undefined;
}

/** May `me` change or remove this person's access? Owners/admins and creators are fixed; inherited ones change only. */
export function canChangePerson(p: PagePermission, caps: SharingCapabilities): { change: boolean; remove: boolean } {
  const fixed = p.source === "owner" || p.source === "admin" || p.source === "creator";
  if (!caps.canShare || fixed) return { change: false, remove: false };
  return { change: true, remove: p.source === "share" };
}

// ---------- Labels ----------

export function accessLabel(a: PageAccess): MessageDescriptor {
  return msg(`sharing.level.${a}`);
}

export function accessDescription(a: PageAccess): MessageDescriptor {
  return msg(`sharing.level.${a}.description`);
}

export function generalLabel(g: GeneralAccess, workspaceName: string): MessageDescriptor {
  return g === "none" ? msg("sharing.general.invitedOnly") : msg("sharing.general.everyone", { workspace: workspaceName });
}

export function generalLevelLabel(g: GeneralAccess): MessageDescriptor {
  return g === "none" ? msg("sharing.general.noAccess") : accessLabel(g);
}

/** "Inherited from <page>" ("…from an untitled page" for pages without a title). */
export function inheritedLabel(title: string): MessageDescriptor {
  return title === "" ? msg("sharing.source.inheritedUntitled") : msg("sharing.source.inherited", { page: title });
}

export function sourceLabel(p: PagePermission): MessageDescriptor | undefined {
  switch (p.source) {
    case "owner":
      return msg("sharing.source.owner");
    case "admin":
      return msg("sharing.source.admin");
    case "creator":
      return msg("sharing.source.creator");
    case "inherited":
      return inheritedLabel(p.inheritedFrom?.title ?? "");
    default:
      return undefined;
  }
}

// ---------- People with access ----------

export type PersonRow = {
  key: string;
  userId: string;
  name: string;
  /** Email for people I may see it for (never shown for others). */
  email?: string;
  avatarUrl?: string;
  isAgent: boolean;
  isGuest: boolean;
  me: boolean;
  access: PageAccess;
  levelLabel: MessageDescriptor;
  note?: MessageDescriptor;
  canChange: boolean;
  canRemove: boolean;
  /** Levels offered in the row's menu. */
  levels: PageAccess[];
};

const SOURCE_ORDER: Record<PermissionSource, number> = { owner: 0, admin: 1, creator: 2, share: 3, inherited: 4 };

/** "People with access", me first, then owners/admins, creator, invited, inherited; by name within each. */
export function peopleRows(sharing: PageSharing, members: Record<string, Member>, meId: string, caps: SharingCapabilities): PersonRow[] {
  const rows = sharing.people.map((p): PersonRow => {
    const m = members[p.userId];
    const allowed = canChangePerson(p, caps);
    const isMe = p.userId === meId;
    const r: PersonRow = {
      key: p.userId,
      userId: p.userId,
      name: m?.displayName ?? "",
      isAgent: m?.kind === "agent",
      isGuest: p.restricted === true || m?.role === "guest",
      me: isMe,
      access: p.access,
      levelLabel: accessLabel(p.access),
      canChange: allowed.change,
      canRemove: allowed.remove,
      levels: allowed.change ? LEVELS.toReversed() : [],
    };
    if (m?.email !== undefined) r.email = m.email;
    if (m?.avatarUrl !== undefined) r.avatarUrl = m.avatarUrl;
    const note = sourceLabel(p);
    if (note !== undefined) r.note = note;
    return r;
  });
  const order = new Map(sharing.people.map((p) => [p.userId, SOURCE_ORDER[p.source]]));
  return rows.toSorted((a, b) => {
    if (a.me !== b.me) return a.me ? -1 : 1;
    const d = (order.get(a.userId) ?? 9) - (order.get(b.userId) ?? 9);
    if (d !== 0) return d;
    const n = a.name.localeCompare(b.name);
    return n !== 0 ? n : a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0;
  });
}

// ---------- Inviting ----------

export type InviteCandidate = {
  key: string;
  kind: "member" | "agent" | "email";
  /** Members and agents. */
  userId?: string;
  /** `email` candidates (someone outside the workspace → "Invite as guest"). */
  email?: string;
  name: string;
  avatarUrl?: string;
  isGuest: boolean;
  /** Already has access on this page (shown, not selectable). */
  hasAccess: boolean;
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isEmail(text: string): boolean {
  return EMAIL.test(text.trim());
}

/**
 * People picker: active members and agents matching `query` (name, handle or visible email), then — when the query is
 * an email nobody here has — that email as an "Invite as guest" candidate (only when `canInviteGuests`).
 */
export function inviteCandidates(
  query: string,
  members: Member[],
  people: PagePermission[],
  meId: string,
  canInviteGuests: boolean,
  limit: number,
): InviteCandidate[] {
  const q = query.trim().toLowerCase();
  const has = new Set(people.map((p) => p.userId));
  const out: InviteCandidate[] = [];
  for (const m of members) {
    if (m.deactivated || m.userId === meId) continue;
    const hay = [m.displayName, m.fullName, m.handle ?? "", m.email ?? ""].map((s) => s.toLowerCase());
    if (q !== "" && !hay.some((h) => h.includes(q))) continue;
    const c: InviteCandidate = {
      key: m.userId,
      kind: m.kind === "agent" ? "agent" : "member",
      userId: m.userId,
      name: m.displayName,
      isGuest: m.role === "guest" || (m.access?.level === "restricted" && m.kind === "human"),
      hasAccess: has.has(m.userId),
    };
    if (m.avatarUrl !== undefined) c.avatarUrl = m.avatarUrl;
    out.push(c);
  }
  out.sort((a, b) => Number(a.hasAccess) - Number(b.hasAccess) || Number(a.kind === "agent") - Number(b.kind === "agent") || a.name.localeCompare(b.name));
  const list = out.slice(0, Math.max(0, limit));
  const email = query.trim().toLowerCase();
  if (canInviteGuests && isEmail(email) && !members.some((m) => m.email?.toLowerCase() === email)) {
    list.unshift({ key: `email:${email}`, kind: "email", email, name: email, isGuest: true, hasAccess: false });
  }
  return list;
}

/** Organization for a guest invited by email: the email's domain ("partner.com"). */
export function organizationFromEmail(email: string): { name: string; domain: string } {
  const domain = email.trim().toLowerCase().split("@")[1] ?? "";
  return { name: domain, domain };
}

// ---------- General access ----------

export type GeneralOption = { value: GeneralAccess; label: MessageDescriptor; description: MessageDescriptor };

export type GeneralControl = {
  value: GeneralAccess;
  /** "Everyone at <workspace>" / "Only people invited". */
  label: MessageDescriptor;
  description: MessageDescriptor;
  /** The level's label ("Can edit"…, "No access" when only people invited). */
  levelLabel: MessageDescriptor;
  editable: boolean;
  /** Where the setting comes from (inherited), or where a private page shared with everyone shows up. */
  note?: MessageDescriptor;
  /** Who: "Everyone at <workspace>" (at the current level, or the section's default) / "Only people invited". */
  options: GeneralOption[];
  /** At what level everyone gets it (shown while it isn't "only people invited"). */
  levels: GeneralOption[];
};

/**
 * The "General access" control, like Notion's: who (everyone at the workspace / only people invited) and at what
 * level. Private pages can be shared with the workspace too (they stay in their owner's Private section). Editable
 * with full access (`canChangeGeneral`).
 */
export function generalControl(sharing: PageSharing, caps: SharingCapabilities, workspaceName: string): GeneralControl {
  const privateSection = sharing.visibility === "private";
  const everyone = msg("sharing.general.everyone.description", { workspace: workspaceName });
  const invited = msg("sharing.general.invitedOnly.description");
  const everyoneLevel: GeneralAccess = sharing.general !== "none" ? sharing.general : privateSection ? "view" : DEFAULT_LEVEL;
  const c: GeneralControl = {
    value: sharing.general,
    label: generalLabel(sharing.general, workspaceName),
    description: sharing.general === "none" ? invited : everyone,
    levelLabel: generalLevelLabel(sharing.general),
    editable: caps.canChangeGeneral,
    options: [
      { value: everyoneLevel, label: generalLabel(everyoneLevel, workspaceName), description: everyone },
      { value: "none", label: generalLabel("none", workspaceName), description: invited },
    ],
    levels: LEVELS.toReversed().map((a): GeneralOption => ({ value: a, label: accessLabel(a), description: accessDescription(a) })),
  };
  if (sharing.generalSource === "inherited" && sharing.generalFrom !== undefined) c.note = inheritedLabel(sharing.generalFrom.title);
  else if (privateSection && sharing.general !== "none") c.note = msg("sharing.general.privateShared");
  return c;
}

// ---------- Indicators ----------

export type ShareIndicator = { icon: "lock" | "users" | "globe"; label: MessageDescriptor };

/** The top bar's "Shared with N" / private / public indicator (N = people besides owners/admins and me). */
export function shareIndicator(sharing: PageSharing, meId: string): ShareIndicator {
  if (sharing.link !== undefined) return { icon: "globe", label: msg("sharing.indicator.public") };
  const n = sharing.people.filter((p) => p.userId !== meId && p.source !== "owner" && p.source !== "admin").length;
  if (sharing.general !== "none") return { icon: "users", label: msg("sharing.indicator.workspace") };
  if (n === 0) return { icon: "lock", label: msg("sharing.indicator.private") };
  return { icon: "users", label: msg("sharing.indicator.sharedWith", { count: n }) };
}

export type PageBadge = "public" | "shared" | "restricted";

/**
 * Sidebar badge of a page row: published, shared (with people, or a private page shared with the workspace), or
 * closed to the workspace ("only people invited").
 */
export function pageBadge(p: Pick<PageNode, "published" | "sharedWith" | "generalAccess" | "visibility">): PageBadge | undefined {
  if (p.published === true) return "public";
  if ((p.sharedWith ?? 0) > 0) return "shared";
  if (p.visibility === "private" && p.generalAccess !== undefined && p.generalAccess !== "none") return "shared";
  if (p.visibility === "workspace" && p.generalAccess === "none") return "restricted";
  return undefined;
}

// ---------- Links ----------

/** Absolute link to a page inside the app (Copy link). */
export function pageLink(origin: string, workspaceSlug: string, pageId: string, kind: PageNode["kind"]): string {
  return kind === "database" ? `${origin}/${workspaceSlug}/db/${pageId}` : `${origin}/${workspaceSlug}/p/${pageId}`;
}

export type ExpiryOption = { key: "never" | "1d" | "7d" | "30d"; label: MessageDescriptor; expiresAt?: number };

const EXPIRY_DAYS: Record<"1d" | "7d" | "30d", number> = { "1d": 1, "7d": 7, "30d": 30 };

/** Link expiry choices from `now`. */
export function expiryOptions(now: number): ExpiryOption[] {
  const out: ExpiryOption[] = [{ key: "never", label: msg("sharing.expiry.never") }];
  for (const key of ["1d", "7d", "30d"] as const) {
    out.push({ key, label: msg("sharing.expiry.days", { count: EXPIRY_DAYS[key] }), expiresAt: now + EXPIRY_DAYS[key] * 86_400_000 });
  }
  return out;
}

/** Is a link with this expiry still usable at `now`? */
export function linkActive(expiresAt: number | undefined, now: number): boolean {
  return expiresAt === undefined || expiresAt > now;
}

// ---------- Files: share to a conversation ----------

export type ShareTarget = {
  channelId: string;
  kind: Channel["kind"];
  /** Channel name (without "#"), or the other people's names for DMs. */
  label: string;
  isDm: boolean;
  /** DMs: the other person (1:1) for the avatar. */
  userId?: string;
};

/**
 * Conversations I can post a file to (unarchived channels I'm in and my DMs), matching `query`, channels first then
 * DMs, by name.
 */
export function shareTargets(
  channels: Channel[],
  memberships: Record<string, ChannelMembership>,
  members: Record<string, Member>,
  meId: string,
  query: string,
  limit: number,
): ShareTarget[] {
  const q = query.trim().toLowerCase().replace(/^#/, "");
  const out: ShareTarget[] = [];
  for (const ch of channels) {
    if (ch.archived || memberships[ch.id] === undefined) continue;
    const isDm = ch.kind === "dm" || ch.kind === "group_dm";
    const others = (ch.dmUserIds ?? []).filter((u) => u !== meId);
    const label = isDm ? others.map((u) => members[u]?.displayName ?? "").filter((n) => n !== "").join(", ") : ch.name;
    if (label === "" || (q !== "" && !label.toLowerCase().includes(q))) continue;
    const t: ShareTarget = { channelId: ch.id, kind: ch.kind, label, isDm };
    if (ch.kind === "dm" && others.length === 1) t.userId = others[0]!;
    out.push(t);
  }
  return out.toSorted((a, b) => Number(a.isDm) - Number(b.isDm) || a.label.localeCompare(b.label) || (a.channelId < b.channelId ? -1 : 1)).slice(0, Math.max(0, limit));
}

/** Why I can't create a public link for a file (undefined when I can, or when there's nothing to say). */
export function fileLinkBlockedReason(sharing: { canShare: boolean; canManageLink: boolean; settings: SharingSettings }): MessageDescriptor | undefined {
  if (sharing.canManageLink) return undefined;
  if (!sharing.canShare) return msg("files.share.noDownload");
  if (sharing.settings.publicFiles === "off") return msg("files.share.publicLink.off");
  return msg("files.share.publicLink.adminsOnly");
}

// ---------- The public page ----------

const PUBLIC_PATH = /^\/s\/([A-Za-z0-9_-]{16,128})\/?$/;

/** The token of a public page link (`/s/<token>`), or undefined for any other path. */
export function publicTokenFromPath(path: string): string | undefined {
  return PUBLIC_PATH.exec(path)?.[1];
}
